/**
 * People → Roles
 *
 * A role is defined by two things an admin cares about: who holds it, and
 * what data it can reach. Both are on the row, so the list answers "who is
 * restricted, and how much" without opening anything.
 *
 * Roles are made here; what a role can see is set in the Data model, on each
 * entity and field, and nowhere else. This page only reports it, so there is
 * one place to change access and one place to read it back per role.
 *
 * Data access is stated as what is *hidden*, not what is allowed. A role with
 * no rules reads everything, which is the honest default to show — an
 * allow-list framing would imply tables are hidden until someone lists them,
 * which is not how the gate behaves.
 *
 * The built-in Admin role is shown but not editable. It exists so the list
 * accounts for every member, and its "Full access" line is the baseline the
 * other rows are read against.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';

import { AppDialog } from '../../components/dialog';
import { buildApiUrl } from '../../config';
import { SnackbarContext } from '../../ui/SnackbarProvider';
import { describeHidden, useDataModelAccess } from '../data-model-access/useDataModelAccess';

export interface RoleItem {
  role_id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  editable: boolean;
  user_count: number;
  denied_table_count: number;
  /** Columns hidden in tables the role can otherwise read. */
  denied_column_count: number;
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('klaris_jwt');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function tenantId(): string | null {
  const raw = localStorage.getItem('klaris_tenant');
  if (!raw) return null;
  try {
    const t = JSON.parse(raw);
    return t.tenant_id || t.id || null;
  } catch {
    return raw;
  }
}

export default function RolesTab() {
  const { notify } = React.useContext(SnackbarContext);
  const navigate = useNavigate();
  const tid = React.useMemo(tenantId, []);
  // What each role cannot see, counted the way the Data model shows it.
  const dmAccess = useDataModelAccess();

  const [roles, setRoles] = React.useState<RoleItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [draft, setDraft] = React.useState({ name: '', description: '' });
  const [saving, setSaving] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  const [deleteRole, setDeleteRole] = React.useState<RoleItem | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const load = React.useCallback(() => {
    if (!tid) return;
    setLoading(true);
    fetch(buildApiUrl(`/api/v1/tenants/${tid}/roles`), { headers: authHeader() })
      .then(async (r) => {
        if (r.status === 403) throw new Error('Only admins can manage roles.');
        if (!r.ok) throw new Error('Could not load roles');
        return r.json();
      })
      .then((data) => {
        setRoles(Array.isArray(data) ? data : []);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [tid]);

  React.useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!tid) return;
    setSaving(true);
    setCreateError(null);
    try {
      const r = await fetch(buildApiUrl(`/api/v1/tenants/${tid}/roles`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          name: draft.name.trim(),
          description: draft.description.trim() || null,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || 'Could not create the role');
      }
      const created: RoleItem = await r.json();
      notify(
        `Role "${created.name}" created. It sees everything until you hide something from it in the Data model.`,
        'success',
      );
      setCreateOpen(false);
      setDraft({ name: '', description: '' });
      load();
      dmAccess.reload();
    } catch (e: any) {
      setCreateError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!tid || !deleteRole) return;
    setDeleting(true);
    try {
      const r = await fetch(
        buildApiUrl(`/api/v1/tenants/${tid}/roles/${deleteRole.role_id}`),
        { method: 'DELETE', headers: authHeader() },
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || 'Could not delete the role');
      }
      notify(`Role "${deleteRole.name}" deleted`, 'success');
      setDeleteRole(null);
      load();
    } catch (e: any) {
      notify(e.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        sx={(t) => ({
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 2 },
          mb: 2.5,
          pb: 1.5,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
        })}
      >
        <Typography variant="body2" color="text.secondary"
          sx={{ fontSize: '0.8125rem', lineHeight: 1.45, flex: 1, minWidth: 0 }}>
          Group members by what they do. What each group can see is set in the Data model, on
          each entity and field.
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddRounded />}
          onClick={() => { setCreateError(null); setCreateOpen(true); }}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2,
                alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
        >
          New role
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      <Paper
        variant="outlined"
        sx={(t) => ({ borderRadius: 2, overflow: 'hidden', borderColor: t.tokens.border.subtle })}
      >
        <Box
          sx={(t) => ({
            px: 2, py: 1.25,
            borderBottom: `1px solid ${t.tokens.border.subtle}`,
            bgcolor: t.tokens.surface?.sunken || t.palette.action.hover,
          })}
        >
          <Typography variant="caption"
            sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                  fontSize: '0.6875rem', color: 'text.secondary' }}>
            Roles{loading ? '' : ` · ${roles.length}`}
          </Typography>
        </Box>

        {loading ? (
          [0, 1].map((i) => (
            <Box key={i} sx={(t) => ({
              display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.75,
              borderBottom: i === 0 ? `1px solid ${t.tokens.border.subtle}` : 'none',
            })}>
              <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1.5 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={150} height={20} />
                <Skeleton variant="text" width={220} height={16} />
              </Box>
              <Skeleton variant="rounded" width={110} height={30} sx={{ borderRadius: 1.5 }} />
            </Box>
          ))
        ) : roles.length === 0 ? (
          <Box sx={{ px: 2.5, py: 5, textAlign: 'center' }}>
            <ShieldRounded sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No roles yet.</Typography>
          </Box>
        ) : (
          roles.map((role, idx) => (
            <Box
              key={role.role_id}
              sx={(t) => ({
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1.25, sm: 2 },
                px: 2, py: 1.75,
                borderBottom: idx < roles.length - 1
                  ? `1px solid ${t.tokens.border.subtle}` : 'none',
              })}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0, flex: 1 }}>
                <Box sx={(t) => ({
                  width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: role.editable ? t.tokens.brand.primarySoft : t.tokens.surface.sunken,
                  border: `1px solid ${t.tokens.border.subtle}`,
                })}>
                  {role.editable
                    ? <ShieldRounded sx={{ fontSize: 17, color: 'primary.main' }} />
                    : <LockRounded sx={{ fontSize: 16, color: 'text.disabled' }} />}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.875} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ textTransform: 'capitalize' }}>
                      {role.name}
                    </Typography>
                    {!role.editable && (
                      <Chip label="Built-in" size="small" variant="outlined"
                        sx={(t) => ({ height: 19, fontSize: '0.6875rem', fontWeight: 600,
                                      color: 'text.secondary', borderColor: t.tokens.border.subtle,
                                      '& .MuiChip-label': { px: 0.75 } })} />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ fontSize: '0.75rem', display: 'block' }} noWrap>
                    {role.description || 'No description'}
                  </Typography>
                </Box>
              </Box>

              {/* Stats and actions, on a fixed grid and behind their own rule.
                  Laid out in flow they drifted row to row: the built-in role
                  has no delete button, so its columns sat further right than
                  every other row's. Each slot is a fixed width and the delete
                  slot is always reserved, empty or not. */}
              <Box
                sx={(t) => ({
                  flexShrink: 0,
                  display: 'grid',
                  gridTemplateColumns: '72px 200px 152px 34px',
                  alignItems: 'center',
                  gap: 2,
                  pl: 2.5,
                  borderLeft: { sm: `1px solid ${t.tokens.border.subtle}` },
                })}
              >
                <Box>
                  <Typography variant="caption" color="text.disabled"
                    sx={{ fontSize: '0.6875rem', textTransform: 'uppercase',
                          letterSpacing: '0.06em', display: 'block' }}>
                    Members
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {role.user_count}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.disabled"
                    sx={{ fontSize: '0.6875rem', textTransform: 'uppercase',
                          letterSpacing: '0.06em', display: 'block' }}>
                    Data access
                  </Typography>
                  {(() => {
                    // Counted in the Data model's terms, since that is where
                    // an admin goes to change it. Wraps: "2 entities, 5 fields
                    // and 1 other table hidden" must not end in "…".
                    const restricted = role.denied_table_count + role.denied_column_count > 0;
                    const text = !role.editable
                      ? 'Sees everything'
                      : dmAccess.access
                        ? describeHidden(dmAccess.access.summary[role.role_id])
                        : restricted ? 'Some data hidden' : 'Sees everything';
                    return (
                      <Typography variant="body2"
                        sx={{ fontWeight: 600, overflowWrap: 'anywhere',
                              color: text === 'Sees everything' ? 'success.dark' : 'warning.dark' }}>
                        {text}
                      </Typography>
                    );
                  })()}
                </Box>

                <Tooltip title={role.editable
                  ? 'Open the Data model to choose which entities and fields this role can see'
                  : 'Built-in roles always see everything'}>
                  <span>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      disabled={!role.editable}
                      endIcon={<ArrowForwardRounded sx={{ fontSize: '15px !important' }} />}
                      onClick={() => navigate('/data-model')}
                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem',
                            borderRadius: 1.5, minHeight: 30, whiteSpace: 'nowrap' }}
                    >
                      Set in Data model
                    </Button>
                  </span>
                </Tooltip>

                {/* Reserved even when empty, so the columns to its left do not
                    shift on the row that has no delete. */}
                {role.editable ? (
                  <Tooltip title="Delete role">
                    <Button
                      size="small"
                      variant="text"
                      color="inherit"
                      onClick={() => setDeleteRole(role)}
                      sx={{ minWidth: 34, px: 0, borderRadius: 1.5, color: 'text.disabled' }}
                    >
                      <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                    </Button>
                  </Tooltip>
                ) : <Box />}
              </Box>
            </Box>
          ))
        )}
      </Paper>

      {/* Create */}
      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        size="sm"
        icon={<ShieldRounded sx={{ color: 'primary.main' }} />}
        title="New role"
        subtitle="Give it a name your team will recognise. It sees everything until you hide something from it in the Data model."
        busy={saving}
        primaryAction={{
          label: saving ? 'Creating…' : 'Create role',
          onClick: handleCreate,
          disabled: !draft.name.trim(),
        }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: () => setCreateOpen(false) }}
      >
        {createError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{createError}</Alert>}
        <Stack spacing={2}>
          <TextField
            label="Name" size="small" fullWidth autoFocus
            placeholder="Sourcing"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
          <TextField
            label="Description" size="small" fullWidth
            placeholder="What this group does"
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          />
        </Stack>
      </AppDialog>

      {/* Delete */}
      <AppDialog
        open={!!deleteRole}
        onClose={() => setDeleteRole(null)}
        size="sm"
        title={`Delete "${deleteRole?.name}"?`}
        subtitle="Its access rules go with it. Members keep their accounts."
        busy={deleting}
        primaryAction={{
          label: deleting ? 'Deleting…' : 'Delete role',
          color: 'error',
          onClick: handleDelete,
        }}
        secondaryAction={{ label: 'Cancel', color: 'inherit', onClick: () => setDeleteRole(null) }}
      >
        {!!deleteRole?.user_count && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            {deleteRole.user_count} member{deleteRole.user_count === 1 ? '' : 's'} still
            {deleteRole.user_count === 1 ? ' has' : ' have'} this role. Move them to another
            role first — otherwise they would be left with no role and full access.
          </Alert>
        )}
      </AppDialog>

    </Box>
  );
}
